/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import IframeResizer from 'iframe-resizer-react';
import classNames from 'classnames';
import { useState, useEffect, useRef, useCallback } from 'react';

import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import {
  getIsThirdPartyMember,
  getUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { openFullScreen } from 'common/components/SelectReport/tableauUtils';
import { Icon } from '@pwc/pwc-deals-ui';
import { isString } from 'lodash';
import { UnableToLoadContent } from 'common/components/UnableToLoadContent';
import { useLiveReportPreview } from '@pwc/pwc-deals-report-ui/build/components/ReportPreview/provider';
import SupportTicketLink from 'common/components/SupportTicketLink';
import { ErrorBox, ErrorBoxProps } from 'common/components/ErrorBox';
import AsyncSharingStatusMessage, {
  LibraryItemTypeEnum,
} from 'common/components/AsyncSharingStatusMessage/AsyncSharingStatusMessage';

import {
  setReportPreviewProps,
  updateLibraryItem,
} from 'features/analyses/libraryItemsSlice';
import SyncedInfo from 'common/components/Autosync/SyncedInfo';
import {
  ILibraryItemData,
  LibraryItemErrorEnum,
} from '../interfaces/ILibraryItemData';
import { IPowerPivotProps } from '../interfaces/IPowerPivotProps';
import './PowerPivotLibraryItemCard.scss';
import { LibraryItemShareStatus } from '../constants/PowerPivotSharingStatus';

declare global {
  interface Document {
    webkitIsFullScreen: unknown;
    mozFullScreen: unknown;
    msFullscreenElement: unknown;
  }
}

export const PowerPivotLibraryItemCard = ({
  libraryItem,
}: IPowerPivotProps) => {
  const dispatch = useDispatch();

  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const { projectId } = useParams<any>(); // eslint-disable-line
  const iframeContainerRef = useRef(null);

  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [isMultiline, setIsMultiline] = useState(false);
  const MULTILINE_THRESHOLD = 88;
  const observedDiv = useRef<HTMLDivElement>(null);
  const reportPreviewData = useLiveReportPreview();
  const image: { placeholderImage?: string } = reportPreviewData;
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);

  const handleElementResized = () => {
    setContainerWidth(observedDiv.current?.offsetWidth ?? 0);
    setContainerHeight(observedDiv.current?.offsetHeight ?? 0);
  };

  useEffect(() => {
    const loadPlaceHolderImage = () => {
      if (dispatch) {
        if (libraryItem) {
          const { id } = libraryItem;
          dispatch(
            updateLibraryItem({
              id,
              item: image.placeholderImage,
            })
          );
        }
        dispatch(
          setReportPreviewProps({
            isDirty: true,
          })
        );
      }
    };
    loadPlaceHolderImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, image.placeholderImage]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(handleElementResized);
    if (observedDiv.current) {
      if (observedDiv.current) {
        resizeObserver.observe(observedDiv.current);
      }
    }
    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [observedDiv.current]);

  useEffect(() => {
    if (containerHeight > MULTILINE_THRESHOLD) {
      setIsMultiline(true);
    } else {
      setIsMultiline(false);
    }
  }, [containerHeight, containerWidth]);

  const [powerPivotData, setPowerPivotData] = useState<ILibraryItemData>();
  const [isShowAutosync, setIsShowAutosync] = useState(false);
  const [isFullscreenHover, setIsFullscreenHover] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasJsonError, setHasJsonError] = useState(false);
  const [errorData, setErrorData] = useState<ErrorBoxProps | undefined>(
    undefined
  );

  let SharingStatusMessage = null;

  if (
    powerPivotData?.status &&
    (powerPivotData.status === LibraryItemShareStatus.IN_PROGRESS ||
      powerPivotData.status === LibraryItemShareStatus.FAILED)
  ) {
    SharingStatusMessage = (
      <AsyncSharingStatusMessage
        status={powerPivotData.status}
        type={LibraryItemTypeEnum.PowerPivot}
      />
    );
  }
  if (isThirdPartyMember && powerPivotData?.hideWorkSheets) {
    SharingStatusMessage = (
      <AsyncSharingStatusMessage
        status={LibraryItemShareStatus.FAILED}
        type={LibraryItemTypeEnum.PowerPivot}
      />
    );
  }

  const isLibraryItemError = powerPivotData?.errorCode;

  const getErrorMessage = useCallback(
    (data: ILibraryItemData): ErrorBoxProps => {
      const errorCode = data?.errorCode;
      if (errorCode === LibraryItemErrorEnum.SharepointConnectionFailure) {
        if (isPwcUser) {
          // Case 1a: When there's an issue with the general connection with Sharepoint (PwC view)
          return {
            title: 'Unable to connect to SharePoint',
            message: (
              <>
                Please try again later. If the issue continues, please{' '}
                <SupportTicketLink>create a support ticket</SupportTicketLink>{' '}
                for assistance.
              </>
            ),
          };
        }
        // Case 1b: When there's an issue with the general connection with Sharepoint (Client view)
        return {
          title: 'Unable to connect to SharePoint',
          message: (
            <>
              Please try again later. If the issue continues, please contact the
              Engagement Lead or Engagement Manager.
            </>
          ),
        };
      }
      if (
        errorCode === LibraryItemErrorEnum.FailedToSaveLibraryItem ||
        errorCode === LibraryItemErrorEnum.FailedToProcessFile
      ) {
        if (isPwcUser) {
          // Case 2a: When there's an issue with the File (such as issue with upload, aspose issue, file being moved/deleted, tab being deleted) (PwC view)
          return {
            title: 'Unable to load worksheet',
            message: (
              <>
                <Box>
                  The worksheet may have been renamed or deleted, or the file
                  may have been moved or deleted. Use the Excel add-in to add
                  the worksheet to Junction again.
                </Box>
                <Box mt="1rem">
                  If the issue continues, please{' '}
                  <SupportTicketLink>create a support ticket</SupportTicketLink>{' '}
                  for assistance.
                </Box>
              </>
            ),
          };
        }
        // Case 2b: When there's an issue with the File (such as issue with upload, aspose issue, file being moved/deleted, tab being deleted) (Client view)
        return {
          title: 'Unable to load worksheet',
          message: (
            <>
              Please try again later. If the issue continues, please contact the
              Engagement Lead or Engagement Manager.
            </>
          ),
        };
      }
      if (errorCode === LibraryItemErrorEnum.GenericError) {
        if (isPwcUser) {
          // Case 3a: If the error does not fall under the first 2 scenarios, then Generic Error Message for eg. "Unable to load" including the library load issue (PwC view)
          return {
            title: 'Unable to load worksheet',
            message: (
              <>
                Please try again later. If the issue continues, please{' '}
                <SupportTicketLink>create a support ticket</SupportTicketLink>{' '}
                for assistance.
              </>
            ),
          };
        }
        // Case 3b: If the error does not fall under the first 2 scenarios, then Generic Error Message for eg. "Unable to load" including the library load issue (Client view)
        return {
          title: 'Unable to load worksheet',
          message: (
            <>
              Please try again later. If the issue continues, please contact the
              Engagement Lead or Engagement Manager.
            </>
          ),
        };
      }
      if (errorCode === LibraryItemErrorEnum.SharingFailed) {
        if (isPwcUser) {
          return {
            title: 'Excel file failed to clone for client view',
            message: (
              <>
                Please{' '}
                <SupportTicketLink>create a support ticket</SupportTicketLink>{' '}
                for assistance with this issue.
              </>
            ),
          };
        }
        return {
          title: 'Unable to load content',
          message: (
            <>
              Please try again later. If the issue continues, please contact the
              Engagement Lead or Engagement Manager.
            </>
          ),
        };
      }
      return {
        title: 'Error',
        message: <>An unknown issue occurred.</>,
      };
    },
    [isPwcUser]
  );

  const exitHandler = useCallback(() => {
    if (
      !document.webkitIsFullScreen &&
      !document.mozFullScreen &&
      !document.msFullscreenElement &&
      isFullscreen
    ) {
      // Run code on exit fullscreen
      setIsFullscreen(false);
    }
  }, [isFullscreen]);

  useEffect(() => {
    setErrorData(undefined);
    try {
      let data: ILibraryItemData;
      if (isString(libraryItem.data)) {
        data = JSON.parse(libraryItem.data) as ILibraryItemData;
      } else {
        data = libraryItem.data;
      }

      if (data) {
        const pivotData: ILibraryItemData = {
          ...data,
          status: libraryItem.status,
          isShared: libraryItem.isShared,
        };

        setPowerPivotData(pivotData);
        if (pivotData.errorCode) {
          const newError = getErrorMessage(pivotData);
          setErrorData(newError);
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Unable to parse dynamic object', e);
      setHasJsonError(true);
    }
  }, [
    libraryItem.data,
    libraryItem.status,
    getErrorMessage,
    libraryItem.isShared,
  ]);

  useEffect(() => {
    window.addEventListener('fullscreenchange', exitHandler, false);
    window.addEventListener('mozfullscreenchange', exitHandler, false);
    window.addEventListener('MSFullscreenChange', exitHandler, false);
    window.addEventListener('webkitfullscreenchange', exitHandler, false);

    return () => {
      window.removeEventListener('fullscreenchange', exitHandler, false);
      window.removeEventListener('mozfullscreenchange', exitHandler, false);
      window.removeEventListener('MSFullscreenChange', exitHandler, false);
      window.removeEventListener('webkitfullscreenchange', exitHandler, false);
    };
  }, [exitHandler]);

  if (isLibraryItemError) {
    return <ErrorBox title={errorData?.title} message={errorData?.message} />;
  }

  return (
    <div className="rich-text-library-item-edit">
      {powerPivotData?.name &&
      powerPivotData?.embeddedUrl &&
      !hasJsonError &&
      !SharingStatusMessage ? (
        <Box ref={iframeContainerRef} display="flex" flexDirection="column">
          <IframeResizer
            log
            style={{ width: '1px', minWidth: '100%', flex: '1' }}
            title={powerPivotData.name}
            src={powerPivotData.embeddedUrl}
            className="power-pivot-library-item-card"
            heightCalculationMethod="lowestElement"
          />
          <Box
            sx={{
              display: isMultiline ? 'block' : 'flex',
              alignItems: isMultiline ? undefined : 'flex-start',
              pb: isMultiline ? undefined : '1rem',
              '& .content-item-link': {
                mb: isMultiline ? 0 : undefined,
              },
            }}
            ref={observedDiv}
          >
            {!powerPivotData?.isShared && !isThirdPartyMember ? (
              <SyncedInfo
                isPwcUser={isPwcUser}
                projectId={projectId as string}
                powerPivot={{
                  name: powerPivotData.name,
                  worksheetName: powerPivotData.worksheetName,
                  sharePointItemUrl:
                    powerPivotData.sharePointItemUrl ||
                    libraryItem.sharePointItemUrl ||
                    '',
                }}
                onShowCallback={(showSyncedInfo) => {
                  setIsShowAutosync(showSyncedInfo);
                }}
              />
            ) : null}
            <Box
              display={isMultiline ? 'block' : 'flex'}
              alignItems={isMultiline ? undefined : 'center'}
              sx={{
                mt: isMultiline ? undefined : '1.68rem',
                ml: isShowAutosync && isMultiline ? '0.5rem' : undefined,
              }}
            >
              {isShowAutosync && !isMultiline ? (
                <Box sx={{ color: 'var(--color-grey)', mx: '0.5rem' }}>|</Box>
              ) : null}
              <Button
                variant="text"
                onClick={() => {
                  if (isFullscreen) {
                    if (document.exitFullscreen) {
                      // eslint-disable-next-line
                      document.exitFullscreen();
                      // eslint-disable-next-line
                    } else if (document.webkitExitFullscreen as any) {
                      // eslint-disable-next-line
                      (document as any).webkitExitFullscreen();
                    } else if (document.mozCancelFullScreen) {
                      // eslint-disable-next-line
                      document.mozCancelFullScreen();
                    } else if (document.msExitFullscreen) {
                      // eslint-disable-next-line
                      document.msExitFullscreen();
                    }
                    setIsFullscreen(false);
                  } else {
                    openFullScreen(iframeContainerRef.current);
                    setIsFullscreen(true);
                  }
                }}
                sx={{
                  whiteSpace: 'nowrap',
                  textDecoration: 'underline',
                  pl: '0.25rem',
                }}
                onMouseEnter={() => {
                  setIsFullscreenHover(true);
                }}
                onMouseLeave={() => {
                  setIsFullscreenHover(false);
                }}
                startIcon={
                  <Icon
                    name="full-screen"
                    width="18px"
                    height="18px"
                    // eslint-disable-next-line
                    className={classNames(
                      isFullscreenHover ? 'fill' : 'outline',
                      'icon-orange-theme'
                    )}
                  />
                }
              >
                {isFullscreen ? 'Exit' : 'View'} fullscreen
              </Button>
            </Box>
          </Box>
        </Box>
      ) : null}

      {SharingStatusMessage && !hasJsonError ? SharingStatusMessage : null}

      {hasJsonError && isPwcUser ? <UnableToLoadContent /> : null}
    </div>
  );
};

export default PowerPivotLibraryItemCard;
